/** @file TileFacts: slot key, size, pivot, facing, the entry's other tiles, the saved tile. */
import type { ImageSlot } from '@zilo/pose3d-format/types/facet';
import type { LoadedBundle } from '@zilo/pose3d-runtime-babylon/bundle/loadedBundle';
import type { ReactElement } from 'react';
import { bandWords } from '../../panels/art/bandWords';
import { directionLabel } from '../../panels/art/directionLabel';
import { facingWords } from '../../panels/art/facingWords';
import { useThumbnail } from '../../panels/art/useThumbnail';
import type { TileRef } from '../../state/editorTypes';
import { Panel } from '../../ui-kit/Panel';

/** The tile's slot, its facing in degrees, the entry's slots (this one included), the bundle, and open-a-tile. */
export type TileFactsProps = {
  slot: ImageSlot; azimuth: number | null; entrySlots: readonly ImageSlot[]; bundle: LoadedBundle | null;
  onOpen: (ref: TileRef) => void;
};

/** A thumbnail button of one tile of the entry: the saved image, or its angle when empty. */
function EntryTile({ slot, current, bundle, onOpen }: { slot: ImageSlot; current: boolean; bundle: LoadedBundle | null; onOpen: (ref: TileRef) => void }): ReactElement {
  const url = useThumbnail(slot.status === 'filled' ? bundle : null, slot);
  return (
    <button type="button" className="p3-tile-thumb" aria-pressed={current} title={`Paint ${slot.key}`} data-slot={slot.key}
      onClick={() => { onOpen({ drawable: slot.drawable, entry: slot.entry, tile: slot.tile }); }}>
      {url === null ? <span className="p3-muted">empty</span> : <img src={url} alt={`tile ${slot.key}`} />}
      <span className="p3-mono">{slot.tile}</span>
    </button>
  );
}

/** The right panel of the Tile screen; the saved tile updates after Save (unsaved strokes are not previewed here). */
export function TileFacts({ slot, azimuth, entrySlots, bundle, onOpen }: TileFactsProps): ReactElement {
  const saved = useThumbnail(slot.status === 'filled' ? bundle : null, slot);
  const row = (k: string, v: string): ReactElement => <tr><th scope="row">{k}</th><td>{v}</td></tr>;
  return (
    <Panel title="Tile">
      <div className="p3-stack" style={{ padding: 10, gap: 10 }}>
        <table className="p3-table p3-tile-facts" aria-label="Tile facts">
          <tbody>
            {row('Slot', slot.key)}
            {row('Size', `${String(slot.size[0])}x${String(slot.size[1])} px`)}
            {row('Pivot', `${String(slot.pivot[0])}, ${String(slot.pivot[1])} px`)}
            {row('Facing', `${directionLabel(azimuth ?? undefined)} · ${facingWords(azimuth ?? undefined)}`)}
            {row('Look', bandWords(slot.bands))}
            {slot.mirroredFrom === undefined ? null : row('Mirror of', slot.mirroredFrom)}
          </tbody>
        </table>
        <span className="p3-muted">Tiles of entry {slot.entry}</span>
        <div className="p3-tile-thumbs">
          {entrySlots.map((s) => <EntryTile key={s.key} slot={s} current={s.key === slot.key} bundle={bundle} onOpen={onOpen} />)}
        </div>
        <span className="p3-muted">In the puppet (after Save)</span>
        <div className="p3-tile-saved" data-testid="saved-tile">
          {saved === null ? <span className="p3-muted">not painted</span> : <img src={saved} alt={`saved ${slot.key}`} />}
        </div>
      </div>
    </Panel>
  );
}
